import React, { useEffect, useState } from "react";
import { CheckCircle2, Link2, Unplug, Info, Copy, RefreshCw } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * Compact per-user Google Calendar connection widget.
 * Shows: "Nicht verbunden" or the connected Google email + Disconnect action.
 */
export default function GoogleCalendarConnect() {
  const [status, setStatus] = useState({ connected: false, email: null });
  const [diag, setDiag] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/calendar/diagnose");
      setStatus({ connected: !!data?.connected, email: data?.email || null });
      setDiag(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const resync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/calendar/resync");
      toast.success(data.pushed ? `${data.pushed} Termin(e) nach Google übertragen` : "Alle Termine waren bereits in Google");
      if (data.last_error) toast.error("Google meldet: " + data.last_error);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSyncing(false); }
  };

  // Show a toast after OAuth redirect (?calendar_connected=1 or ?calendar_error=…)
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get("calendar_connected")) {
      toast.success("Google Kalender verbunden");
      url.searchParams.delete("calendar_connected");
      window.history.replaceState({}, "", url.pathname + (url.search ? "?" + url.searchParams.toString() : ""));
      load();
    }
    const err = url.searchParams.get("calendar_error");
    if (err) {
      toast.error("Verbindung fehlgeschlagen: " + err);
      url.searchParams.delete("calendar_error");
      window.history.replaceState({}, "", url.pathname + (url.search ? "?" + url.searchParams.toString() : ""));
    }
  }, []);

  const connect = () => {
    // Full-page redirect so Google OAuth can set cookies etc.
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/calendar/oauth/start`;
  };

  const disconnect = async () => {
    if (!window.confirm("Google Kalender wirklich trennen? Neue Termine werden nicht mehr synchronisiert.")) return;
    try {
      await api.post("/calendar/oauth/disconnect");
      toast.success("Verbindung entfernt");
      setStatus({ connected: false, email: null });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  if (loading) return null;

  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const redirectUri = `${backendUrl}/api/calendar/oauth/callback`;

  if (!status.connected) {
    return (
      <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={connect}
          data-testid="calendar-connect-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 text-xs font-medium border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white transition-colors"
        >
          <Link2 size={12} strokeWidth={2} /> Alle AVGS-Termine mit Google verbinden
        </button>
        <details className="relative">
          <summary className="cursor-pointer inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-overline text-slate-500 hover:text-slate-900 py-2">
            <Info size={11} strokeWidth={1.5} /> Setup-Hilfe
          </summary>
          <div className="absolute z-10 mt-1 left-0 border border-slate-200 bg-white shadow-lg p-4 w-[min(420px,calc(100vw-2rem))] text-xs" data-testid="gc-setup-hint">
            <div className="font-bold text-slate-900 mb-2">Einmalig in Google Cloud Console einrichten</div>
            <ol className="list-decimal pl-4 space-y-1 text-slate-700">
              <li><a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer" className="underline">Google Cloud Console → APIs & Dienste → Credentials</a></li>
              <li>OAuth-Client öffnen → „Authorized redirect URIs" → diesen Wert hinzufügen:</li>
            </ol>
            <div className="mt-2 flex items-center gap-2 bg-slate-50 border border-slate-200 px-2 py-1 font-mono text-[11px]">
              <span className="flex-1 break-all">{redirectUri}</span>
              <button
                data-testid="copy-redirect-uri"
                onClick={() => { navigator.clipboard.writeText(redirectUri); toast.success("Kopiert"); }}
                className="text-slate-500 hover:text-slate-900"
              >
                <Copy size={12} strokeWidth={1.5} />
              </button>
            </div>
            <ol start={3} className="list-decimal pl-4 mt-2 space-y-1 text-slate-700">
              <li>OAuth Consent Screen → Scope <code className="bg-slate-100 px-1">…/auth/calendar</code> aktivieren</li>
              <li>Speichern → dann hier auf „Verbinden" klicken</li>
            </ol>
          </div>
        </details>
      </div>
      {diag?.hint && (
        <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2" data-testid="gc-diagnose-hint">
          {diag.hint}
        </div>
      )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="inline-flex items-center gap-2 px-4 py-2.5 border border-emerald-300 bg-emerald-50 text-emerald-900 text-xs w-fit" data-testid="calendar-connected-badge">
        <CheckCircle2 size={12} strokeWidth={2} />
        <span className="font-medium">Google Kalender</span>
        {status.email && <span className="text-emerald-700 truncate max-w-[220px]">· {status.email}</span>}
        {diag && <span className="text-emerald-700" data-testid="gc-sync-count">· {diag.appointments_in_google}/{diag.appointments_upcoming} Termine übertragen</span>}
        <button onClick={resync} disabled={syncing} className="ml-1 text-emerald-800 hover:text-emerald-900 disabled:opacity-50 inline-flex items-center gap-1" title="Noch nicht übertragene Termine jetzt nach Google schieben" data-testid="calendar-resync-btn">
          <RefreshCw size={12} strokeWidth={1.5} className={syncing ? "animate-spin" : ""} /> {syncing ? "Überträgt…" : "Termine jetzt übertragen"}
        </button>
        <button onClick={disconnect} className="ml-1 text-emerald-700 hover:text-red-700" title="Trennen" data-testid="calendar-disconnect-btn">
          <Unplug size={12} strokeWidth={1.5} />
        </button>
      </div>
      {(diag?.last_error || diag?.token_ok === false || diag?.has_calendar_scope === false) && (
        <div className="text-[11px] text-red-800 bg-red-50 border border-red-200 px-3 py-2" data-testid="gc-diagnose-error">
          {diag.hint}
          {diag.last_error && <div className="mt-1 font-mono text-[10px] text-red-700 break-all">{diag.last_error}</div>}
        </div>
      )}
    </div>
  );
}
